import { useState } from "react";

import { createSurface } from "../materials/BaseMaterial";

export default function useRenderingState() {
  /*
   * Viewer.
   */
  const [mode, setMode] = useState("render");

  const [technique, setTechnique] = useState("pbr");

  const [modelId, setModelId] = useState("torus-knot");

  /*
   * Surface.
   */
  const [surface, setSurface] = useState(() => createSurface());

  const [textureId, setTextureId] = useState("none");

  /*
   * Lighting.
   */
  const [lighting, setLighting] = useState({
    color: "#ffffff",
    intensity: 1.0,
    ambient: 0.15,

    direction: [0.5, 0.8, 1.0],
  });

  /*
   * Scene.
   */
  const [scene, setScene] = useState({
    autoRotate: false,
  });

  /*
   * Technique state.
   */
  const [techniques, setTechniques] = useState({
    hatching: {
      inkColor: "#111111",
      hatchScale: 1.75,
    },

    toon: {
      bands: 4,
    },

    gooch: {
      coolColor: "#3366cc",
      warmColor: "#ffb347",
      coolMix: 0.25,
      warmMix: 0.25,
    },

    halftone: {
      inkColor: "#111111",
      dotSize: 8.0,
      angle: 45,
      softness: 0.08,
    },

    outline: {
      lineColor: "#151515",
      silhouetteWidth: 0.25,
      detailStrength: 1.0,
      shadingStrength: 0.35,
    },

    pbr: {
      metallic: 0.0,
      roughness: 0.5,
      metallicMap: null,
      roughnessMap: null,
    },
  });

  /*
   * Update one surface property.
   */
  function updateSurface(property, value) {
    setSurface((current) => ({
      ...current,
      [property]: value,
    }));
  }

  /*
   * Update one lighting property.
   */
  function updateLighting(property, value) {
    setLighting((current) => ({
      ...current,
      [property]: value,
    }));
  }

  /*
   * Update one component of
   * the light direction.
   */
  function updateLightDirection(index, value) {
    setLighting((current) => {
      const direction = [...current.direction];

      direction[index] = value;

      return {
        ...current,
        direction,
      };
    });
  }

  /*
   * Update one scene property.
   */
  function updateScene(property, value) {
    setScene((current) => ({
      ...current,
      [property]: value,
    }));
  }

  /*
   * Update one property belonging
   * to a rendering technique.
   */
  function updateTechnique(techniqueId, property, value) {
    setTechniques((current) => ({
      ...current,

      [techniqueId]: {
        ...current[techniqueId],

        [property]: value,
      },
    }));
  }

  /*
   * Texture resources need to be
   * disposed when replaced.
   */
  function updateTechniqueTexture(techniqueId, property, texture) {
    setTechniques((current) => {
      const oldTexture = current[techniqueId][property];

      if (oldTexture && oldTexture !== texture) {
        oldTexture.dispose();
      }

      return {
        ...current,

        [techniqueId]: {
          ...current[techniqueId],

          [property]: texture,
        },
      };
    });
  }

  return {
    mode,
    setMode,

    technique,
    setTechnique,

    modelId,
    setModelId,

    surface,
    setSurface,
    updateSurface,

    textureId,
    setTextureId,

    lighting,
    updateLighting,
    updateLightDirection,

    scene,
    updateScene,

    techniques,
    updateTechnique,
    updateTechniqueTexture,
  };
}
